import {
    CVData,
    CVLayout,
    Education,
    Experience,
    Interest,
    Language,
    Reference,
    SectionKey,
    Skill,
} from '../types/cv';

export const A4_PAGE_WIDTH_MM = 210;
export const A4_PAGE_HEIGHT_MM = 297;
/** Hauteur A4 en px CSS (~96dpi) : 297mm ≈ 1123px — pas 842 (trop court → pagination prématurée). */
export const A4_PAGE_HEIGHT_PX = Math.round((297 / 25.4) * 96);
export const A4_PAGE_WIDTH_PX = Math.round((210 / 25.4) * 96);
export const SUMMARY_ITEM_ID = 'summary';

export const SECTION_LABELS: Record<SectionKey, string> = {
    summary: 'Profil / Résumé',
    experience: 'Expériences',
    education: 'Formations',
    skills: 'Compétences',
    languages: 'Langues',
    interests: "Centres d'intérêt",
    references: 'Références',
};

export const createItemId = (prefix = 'item'): string =>
    `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

const withIds = <T extends { id?: string }>(items: T[] | undefined, prefix: string): T[] =>
    (items || []).map((item) => (item.id ? item : { ...item, id: createItemId(prefix) }));

/** Garantit des ids stables et un layout minimal sur un CV. */
export function ensureCVIdsAndLayout(data: CVData): CVData {
    const experience = withIds(data.experience, 'exp');
    const education = withIds(data.education, 'edu');
    const skills = withIds(data.skills, 'skill');
    const languages = withIds(data.languages, 'lang');
    const interests = withIds(data.interests, 'interest');
    const references = withIds(data.references, 'ref');

    const layout: CVLayout = {
        pageCount: Math.max(1, data.layout?.pageCount || 1),
        itemPages: { ...(data.layout?.itemPages || {}) },
        sectionPages: { ...(data.layout?.sectionPages || {}) },
    };

    const allItems: { id: string; section: SectionKey }[] = [
        ...experience.map((i) => ({ id: i.id!, section: 'experience' as const })),
        ...education.map((i) => ({ id: i.id!, section: 'education' as const })),
        ...skills.map((i) => ({ id: i.id!, section: 'skills' as const })),
        ...languages.map((i) => ({ id: i.id!, section: 'languages' as const })),
        ...interests.map((i) => ({ id: i.id!, section: 'interests' as const })),
        ...references.map((i) => ({ id: i.id!, section: 'references' as const })),
        { id: SUMMARY_ITEM_ID, section: 'summary' },
    ];

    for (const { id, section } of allItems) {
        if (layout.itemPages[id] === undefined) {
            // Nouvel élément : même page que le dernier de sa section (continuité du bloc)
            let maxPage = 0;
            for (const s of allItems) {
                if (s.section === section && s.id !== id && layout.itemPages[s.id] !== undefined) {
                    maxPage = Math.max(maxPage, layout.itemPages[s.id]);
                }
            }
            layout.itemPages[id] = maxPage;
        }
    }

    // pageCount au moins jusqu'à la dernière page utilisée
    const maxUsed = Math.max(0, ...Object.values(layout.itemPages));
    layout.pageCount = Math.max(layout.pageCount, maxUsed + 1);

    return {
        ...data,
        experience,
        education,
        skills,
        languages,
        interests,
        references,
        layout,
    };
}

export function getItemPage(
    layout: CVLayout | undefined,
    itemId: string,
    section: SectionKey
): number {
    if (!layout) return 0;
    if (layout.itemPages[itemId] !== undefined) return layout.itemPages[itemId];
    if (layout.sectionPages?.[section] !== undefined) return layout.sectionPages[section]!;
    return 0;
}

function filterByPage<T extends { id?: string }>(
    items: T[] | undefined,
    pageIndex: number,
    section: SectionKey,
    layout: CVLayout | undefined
): T[] {
    return (items || []).filter((item) => {
        const id = item.id;
        if (!id) return pageIndex === 0;
        return getItemPage(layout, id, section) === pageIndex;
    });
}

export function hasUserStartedEditing(data: CVData): boolean {
    const p = data.personalInfo || ({} as CVData['personalInfo']);
    return !!(
        (p.firstName && p.firstName.trim()) ||
        (p.lastName && p.lastName.trim()) ||
        (p.email && p.email.trim()) ||
        (p.phone && p.phone.trim()) ||
        (p.summary && p.summary.trim()) ||
        (p.profession && p.profession.trim()) ||
        (p.address && p.address.trim()) ||
        (p.city && p.city.trim()) ||
        (p.website && p.website.trim()) ||
        p.profileImage ||
        (data.experience && data.experience.length > 0) ||
        (data.education && data.education.length > 0) ||
        (data.skills && data.skills.length > 0) ||
        (data.languages && data.languages.length > 0) ||
        (data.interests && data.interests.length > 0) ||
        (data.references && data.references.length > 0)
    );
}

/** Construit les données affichées pour une page donnée. */
export function buildPageData(data: CVData, pageIndex: number): CVData {
    const layout = data.layout;
    const isContinuation = pageIndex > 0;

    // Compétences / langues / expériences filtrées par page (colonnes indépendantes).
    // Page 2+ : pas de photo / contact / résumé (restent page 1), mais la sidebar
    // peut continuer avec les compétences assignées à cette page.
    if (isContinuation) {
        return {
            ...data,
            personalInfo: {
                ...data.personalInfo,
                profileImage: undefined,
                summary: '',
                phone: '',
                email: '',
                address: '',
                website: '',
            },
            experience: filterByPage(data.experience, pageIndex, 'experience', layout) as Experience[],
            education: filterByPage(data.education, pageIndex, 'education', layout) as Education[],
            interests: filterByPage(data.interests, pageIndex, 'interests', layout) as Interest[],
            references: filterByPage(data.references, pageIndex, 'references', layout) as Reference[],
            skills: filterByPage(data.skills, pageIndex, 'skills', layout) as Skill[],
            languages: filterByPage(data.languages, pageIndex, 'languages', layout) as Language[],
            layout,
        };
    }

    return {
        ...data,
        personalInfo: {
            ...data.personalInfo,
        },
        experience: filterByPage(data.experience, pageIndex, 'experience', layout) as Experience[],
        education: filterByPage(data.education, pageIndex, 'education', layout) as Education[],
        interests: filterByPage(data.interests, pageIndex, 'interests', layout) as Interest[],
        references: filterByPage(data.references, pageIndex, 'references', layout) as Reference[],
        skills: filterByPage(data.skills, pageIndex, 'skills', layout) as Skill[],
        languages: filterByPage(data.languages, pageIndex, 'languages', layout) as Language[],
        layout,
    };
}

/**
 * Flux par défaut (ordre CV classique) — surchargé par template.
 * La pagination doit suivre l'ordre VISUEL du modèle, sinon pages vides / sections intercalées.
 */
export const MAIN_FLOW_ORDER: SectionKey[] = [
    'experience',
    'education',
    'languages',
    'interests',
    'references',
];

export const SIDEBAR_OVERFLOW_ORDER: SectionKey[] = ['skills'];
/** @deprecated alias */
export const MAIN_OVERFLOW_ORDER = MAIN_FLOW_ORDER;

export type TemplateColumnFlow = {
    main: SectionKey[];
    sidebar: SectionKey[];
};

/** Ordre réel d'affichage par template (colonne principale + sidebar). */
export const TEMPLATE_COLUMN_FLOWS: Record<string, TemplateColumnFlow> = {
    // Références non trackées : la colonne gauche de ce modèle n'a pas de rendu
    // de continuation pour elles (elles disparaîtraient si déplacées en page 2).
    'ocean-wave': {
        main: ['experience', 'education', 'interests'],
        sidebar: ['skills'],
    },
    'modern-blue': {
        main: ['experience', 'education', 'interests'],
        sidebar: ['skills'],
    },
    'geometric-red': {
        main: ['experience', 'education'],
        sidebar: [],
    },
    'creative-crimson': {
        main: ['experience', 'references'],
        sidebar: ['education', 'languages', 'skills'],
    },
    'professional-edge': {
        main: ['education', 'experience'],
        sidebar: ['skills', 'languages', 'references'],
    },
    'red-hexagon': {
        main: ['education', 'experience'],
        sidebar: ['references', 'skills'],
    },
    'corporate-blue': {
        main: ['experience', 'education'],
        sidebar: ['skills', 'references', 'languages'],
    },
    'teal-professional': {
        main: ['education', 'experience', 'skills'],
        sidebar: ['languages', 'references'],
    },
    'earth-tones': {
        main: ['education', 'experience', 'languages'],
        sidebar: ['skills'],
    },
    'sky-blue': {
        main: ['education', 'experience'],
        sidebar: ['skills', 'languages', 'interests'],
    },
    'golden-curve': {
        main: ['experience', 'education', 'languages', 'references'],
        sidebar: ['skills', 'interests'],
    },
    'crimson-wave': {
        main: ['experience', 'education', 'interests'],
        sidebar: ['languages', 'skills'],
    },
    'magenta-ribbon': {
        main: ['experience', 'references'],
        sidebar: ['education', 'languages', 'skills', 'interests'],
    },
    'red-navy': {
        main: ['education', 'experience'],
        sidebar: [],
    },
    'ivoirien-elegant': {
        main: ['experience', 'education'],
        sidebar: ['skills', 'languages'],
    },
    'abidjan-corporate': {
        main: ['experience', 'education'],
        sidebar: ['skills', 'languages', 'references'],
    },
    'sahel-modern': {
        main: ['experience', 'education'],
        sidebar: ['skills', 'interests'],
    },
};

export function getTemplateFlow(templateId?: string): TemplateColumnFlow {
    if (templateId && TEMPLATE_COLUMN_FLOWS[templateId]) {
        return TEMPLATE_COLUMN_FLOWS[templateId];
    }
    return { main: MAIN_FLOW_ORDER, sidebar: SIDEBAR_OVERFLOW_ORDER };
}

export type OverflowColumn = 'sidebar' | 'main' | 'any';

/** Liste plate des items d'une colonne dans l'ordre d'affichage du template. */
export function listFlowItems(
    data: CVData,
    order: SectionKey[]
): { itemId: string; section: SectionKey }[] {
    const ensured = ensureCVIdsAndLayout(data);
    const out: { itemId: string; section: SectionKey }[] = [];
    for (const section of order) {
        for (const it of getSectionItems(ensured, section)) {
            if (it.id) out.push({ itemId: it.id, section });
        }
    }
    return out;
}

/** Liste plate des items main dans l'ordre d'affichage du document. */
export function listMainFlowItems(
    data: CVData,
    templateId?: string
): { itemId: string; section: SectionKey }[] {
    return listFlowItems(data, getTemplateFlow(templateId).main);
}

export function listSidebarFlowItems(
    data: CVData,
    templateId?: string
): { itemId: string; section: SectionKey }[] {
    return listFlowItems(data, getTemplateFlow(templateId).sidebar);
}

function findCandidateInOrder(
    data: CVData,
    fromPage: number,
    order: SectionKey[],
    fromEnd: boolean
): { itemId: string; section: SectionKey } | null {
    const ensured = ensureCVIdsAndLayout(data);
    const layout = ensured.layout!;
    const sections = fromEnd ? [...order].reverse() : order;

    for (const section of sections) {
        const items = getSectionItems(ensured, section).filter((it) => it.id);
        const list = fromEnd ? [...items].reverse() : items;
        for (const it of list) {
            if (getItemPage(layout, it.id, section) === fromPage) {
                return { itemId: it.id, section };
            }
        }
    }
    return null;
}

/** Dernier item du flux sur `fromPage` (celui tout en bas → à faire déborder). */
export function findOverflowCandidate(
    data: CVData,
    fromPage: number,
    column: OverflowColumn = 'any',
    templateId?: string
): { itemId: string; section: SectionKey } | null {
    const flow = getTemplateFlow(templateId);
    if (column === 'sidebar') {
        return findCandidateInOrder(data, fromPage, flow.sidebar, true);
    }
    if (column === 'main' || column === 'any') {
        const ensured = ensureCVIdsAndLayout(data);
        const layout = ensured.layout!;
        const onPage = listMainFlowItems(ensured, templateId).filter(
            (it) => getItemPage(layout, it.itemId, it.section) === fromPage
        );
        if (onPage.length > 0) {
            return onPage[onPage.length - 1];
        }
        if (column === 'main') return null;
        return findCandidateInOrder(data, fromPage, flow.sidebar, true);
    }
    return null;
}

/** Premier item du flux sur `fromPage` (à remonter si de la place se libère). */
export function findPullBackCandidate(
    data: CVData,
    fromPage: number,
    column: OverflowColumn = 'any',
    templateId?: string
): { itemId: string; section: SectionKey } | null {
    if (fromPage <= 0) return null;
    const flow = getTemplateFlow(templateId);
    if (column === 'sidebar') {
        return findCandidateInOrder(data, fromPage, flow.sidebar, false);
    }
    if (column === 'main' || column === 'any') {
        const ensured = ensureCVIdsAndLayout(data);
        const layout = ensured.layout!;
        const onPage = listMainFlowItems(ensured, templateId).filter(
            (it) => getItemPage(layout, it.itemId, it.section) === fromPage
        );
        if (onPage.length > 0) {
            return onPage[0];
        }
        if (column === 'main') return null;
        return findCandidateInOrder(data, fromPage, flow.sidebar, false);
    }
    return null;
}

/** Tout le contenu revient sur la page 1 (corrige les layouts cassés). */
export function resetAllItemsToFirstPage(data: CVData): CVData {
    const ensured = ensureCVIdsAndLayout(data);
    const itemPages: Record<string, number> = {};
    Object.keys(ensured.layout!.itemPages).forEach((id) => {
        itemPages[id] = 0;
    });
    return {
        ...ensured,
        layout: {
            pageCount: 1,
            itemPages,
            sectionPages: {},
        },
    };
}

/**
 * True si les pages du flux principal sont croissantes dans l'ordre d'affichage
 * du template. Sinon le CV affiche des sections « en désordre » et des pages vides.
 */
export function isMainFlowMonotonic(data: CVData, templateId?: string): boolean {
    const ensured = ensureCVIdsAndLayout(data);
    const layout = ensured.layout!;
    let prev = 0;
    for (const it of listMainFlowItems(ensured, templateId)) {
        const p = getItemPage(layout, it.itemId, it.section);
        if (p < prev) return false;
        prev = p;
    }
    return true;
}

export function isSidebarFlowMonotonic(data: CVData, templateId?: string): boolean {
    const ensured = ensureCVIdsAndLayout(data);
    const layout = ensured.layout!;
    let prev = 0;
    for (const it of listSidebarFlowItems(ensured, templateId)) {
        const p = getItemPage(layout, it.itemId, it.section);
        if (p < prev) return false;
        prev = p;
    }
    return true;
}

/** Remet le flux du template (main + sidebar) sur la page 1. */
export function resetMainFlowToFirstPage(data: CVData, templateId?: string): CVData {
    const ensured = ensureCVIdsAndLayout(data);
    const itemPages = { ...ensured.layout!.itemPages };
    for (const it of listMainFlowItems(ensured, templateId)) {
        itemPages[it.itemId] = 0;
    }
    for (const it of listSidebarFlowItems(ensured, templateId)) {
        itemPages[it.itemId] = 0;
    }
    return compactPageCount({
        ...ensured,
        layout: {
            ...ensured.layout!,
            itemPages,
            sectionPages: {},
        },
    });
}

/** Nombre d'éléments de contenu principal (pour décider si la pagination a un sens). */
export function countMainContentItems(data: CVData): number {
    return (
        (data.experience?.length || 0) +
        (data.education?.length || 0) +
        (data.skills?.length || 0) +
        (data.languages?.length || 0) +
        (data.interests?.length || 0) +
        (data.references?.length || 0)
    );
}

/** Compacte : pageCount = dernière page non vide + 1. */
export function compactPageCount(data: CVData): CVData {
    const ensured = ensureCVIdsAndLayout(data);
    let last = 0;
    const maxCheck = Math.max(ensured.layout!.pageCount, 1);
    for (let i = 0; i < maxCheck; i++) {
        if (i === 0 || pageHasContent(ensured, i)) last = i;
    }
    return setPageCount(ensured, last + 1);
}

export function moveItemToPage(data: CVData, itemId: string, pageIndex: number): CVData {
    const ensured = ensureCVIdsAndLayout(data);
    const pageCount = Math.max(ensured.layout!.pageCount, pageIndex + 1);
    return {
        ...ensured,
        layout: {
            ...ensured.layout!,
            pageCount,
            itemPages: {
                ...ensured.layout!.itemPages,
                [itemId]: pageIndex,
            },
        },
    };
}

export function moveSectionToPage(data: CVData, section: SectionKey, pageIndex: number): CVData {
    const ensured = ensureCVIdsAndLayout(data);
    const pageCount = Math.max(ensured.layout!.pageCount, pageIndex + 1);
    const itemPages = { ...ensured.layout!.itemPages };

    const assignAll = (items: { id?: string }[] | undefined) => {
        (items || []).forEach((item) => {
            if (item.id) itemPages[item.id] = pageIndex;
        });
    };

    switch (section) {
        case 'experience':
            assignAll(ensured.experience);
            break;
        case 'education':
            assignAll(ensured.education);
            break;
        case 'skills':
            assignAll(ensured.skills);
            break;
        case 'languages':
            assignAll(ensured.languages);
            break;
        case 'interests':
            assignAll(ensured.interests);
            break;
        case 'references':
            assignAll(ensured.references);
            break;
        case 'summary':
            itemPages[SUMMARY_ITEM_ID] = pageIndex;
            break;
    }

    return {
        ...ensured,
        layout: {
            ...ensured.layout!,
            pageCount,
            itemPages,
            sectionPages: {
                ...ensured.layout!.sectionPages,
                [section]: pageIndex,
            },
        },
    };
}

export function setPageCount(data: CVData, pageCount: number): CVData {
    const ensured = ensureCVIdsAndLayout(data);
    return {
        ...ensured,
        layout: {
            ...ensured.layout!,
            pageCount: Math.max(1, pageCount),
        },
    };
}

export function getSectionItems(
    data: CVData,
    section: SectionKey
): { id: string; label: string }[] {
    switch (section) {
        case 'experience':
            return (data.experience || []).map((e) => ({
                id: e.id || '',
                label: e.position || e.company || 'Expérience',
            }));
        case 'education':
            return (data.education || []).map((e) => ({
                id: e.id || '',
                label: e.degree || e.institution || 'Formation',
            }));
        case 'skills':
            return (data.skills || []).map((e) => ({
                id: e.id || '',
                label: e.name || 'Compétence',
            }));
        case 'languages':
            return (data.languages || []).map((e) => ({
                id: e.id || '',
                label: e.name || 'Langue',
            }));
        case 'interests':
            return (data.interests || []).map((e) => ({
                id: e.id || '',
                label: e.name || 'Intérêt',
            }));
        case 'references':
            return (data.references || []).map((e) => ({
                id: e.id || '',
                label: e.name || 'Référence',
            }));
        case 'summary':
            return data.personalInfo.summary
                ? [{ id: SUMMARY_ITEM_ID, label: 'Résumé du profil' }]
                : [];
        default:
            return [];
    }
}

/** Retrouve la section d’un item à partir de son id. */
export function findSectionForItemId(data: CVData, itemId: string): SectionKey | null {
    if (itemId === SUMMARY_ITEM_ID) return 'summary';
    const sections: SectionKey[] = [
        'experience',
        'education',
        'skills',
        'languages',
        'interests',
        'references',
    ];
    for (const section of sections) {
        if (getSectionItems(data, section).some((it) => it.id === itemId)) {
            return section;
        }
    }
    return null;
}

export function pageHasContent(data: CVData, pageIndex: number): boolean {
    const layout = data.layout;
    const has = (items: { id?: string }[] | undefined, section: SectionKey) =>
        (items || []).some((item) => item.id && getItemPage(layout, item.id, section) === pageIndex);

    return (
        has(data.experience, 'experience') ||
        has(data.education, 'education') ||
        has(data.skills, 'skills') ||
        has(data.languages, 'languages') ||
        has(data.interests, 'interests') ||
        has(data.references, 'references')
    );
}

export function pageHasMainContent(data: CVData, pageIndex: number): boolean {
    const layout = data.layout;
    const has = (items: { id?: string }[] | undefined, section: SectionKey) =>
        (items || []).some((item) => item.id && getItemPage(layout, item.id, section) === pageIndex);
    return (
        has(data.experience, 'experience') ||
        has(data.education, 'education') ||
        has(data.languages, 'languages') ||
        has(data.interests, 'interests') ||
        has(data.references, 'references')
    );
}

/**
 * True si des éléments de cette section existent déjà sur une page précédente.
 * → ne pas réafficher le grand titre, seulement une mention « suite ».
 */
export function isSectionContinuation(
    fullData: CVData | undefined,
    section: SectionKey,
    pageIndex: number
): boolean {
    if (!fullData || pageIndex <= 0) return false;
    const layout = fullData.layout;
    const items = getSectionItems(fullData, section);
    return items.some(
        (it) => it.id && getItemPage(layout, it.id, section) < pageIndex
    );
}
